import { Component, OnInit } from '@angular/core';
import { CreateInventoryModal } from '@inventory/constants/inventory-constants';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { CalendarV2ModalComponent } from '@shared/components/calendar-v2-modal/calendar-v2-modal.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { CalendarSetup } from '@shared/models/calendar-setup-model';
import { ModalService } from '@shared/services/modal/modal.service';
import { InventoryStatus, SubCategory } from '@usf/inventory-types';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ListsViewModel } from '../../../lists/model/lists-view.model';
import {
  AddToInventoryViewModel,
  SelectedProduct,
} from '../../models/add-to-inventory-view-model.model';
import { CreateInventoryModalValues } from '../../models/create-inventory.model';
import { InventoryViewModel } from '../../models/inventory-view.model';
import { PopulateInventoryDesktopTabletComponent } from './components/inventory-modals/populate-inventory/populate-inventory-desktop-tablet/populate-inventory-desktop-tablet.component';
import { PopulateInventoryMobileComponent } from './components/inventory-modals/populate-inventory/populate-inventory-mobile/populate-inventory-mobile.component';
import { CreateInventoryService } from './services/create-inventory.service';
import { CreateInventoryValidationErrors } from '@inventory/models/validation-errors-model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-create-inventory',
  templateUrl: './create-inventory.page.html',
  styleUrls: ['./create-inventory.page.scss'],
})
export class CreateInventoryPage
  implements OnInit, ViewWillEnter, ViewWillLeave {
  breadcrumbs: Crumb[];
  selectedProductsModalIsOpen$ = new BehaviorSubject(false);
  addToInventoryViewModel$: Observable<AddToInventoryViewModel>;
  selectedInventory$: Observable<InventoryViewModel>;
  validationErrors$: Observable<CreateInventoryValidationErrors>;
  InventoryStatus = InventoryStatus;

  constructor(
    readonly createInventoryService: CreateInventoryService,
    readonly platformService: PlatformService,
    private modalController: ModalController,
    private modalService: ModalService,
    private panAppState: PanAppState,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.selectedInventory$ = this.createInventoryService
      .selectSelectedInventory$()
      .pipe(
        tap(selectedInventory => {
          if (
            selectedInventory !== undefined &&
            selectedInventory?.inventoryId !==
              this.createInventoryService.createInventoryForm.get(
                'selectedInventory',
              ).value?.inventoryId
          ) {
            this.handleInventorySelection(selectedInventory);
          } else if (
            selectedInventory === undefined &&
            this.createInventoryService.createInventoryForm.get(
              'selectedInventory',
            ).value !== undefined
          ) {
            this.createInventoryService.createInventoryForm.patchValue({
              selectedInventory: undefined,
            });
          }
        }),
      );
  }

  ionViewWillEnter(): void {
    this.createInventoryService.clearValidation();
    this.setBreadcrumbs(
      this.translateService.instant(PageTitle.createInventory),
    );
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.createInventory),
    );
    this.createInventoryService.setupValues();
    this.validationErrors$ = this.createInventoryService.nameDateIssue$();
    this.createInventoryService.trackCreateInventoryPageLoaded();
  }

  changeResetCounts() {
    this.createInventoryService.resetCounts = !this.createInventoryService
      .resetCounts;
  }

  setBreadcrumbs(lastLabel: string) {
    var homeCrumbUrl = this.platformService.isTouch.value
      ? '/touch/home'
      : '/desktop/home';
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.inventory',
        url: '/' + PATHS.INVENTORY,
        active: false,
      },
      {
        label: lastLabel,
        url: '',
        active: true,
      },
    ];
  }

  ionViewWillLeave(): void {
    this.createInventoryService.clearValues();
    this.createInventoryService.updateSelectedInventory('', false);
    this.createInventoryService.clearValidation();
    this.createInventoryService.clearRecentPurchase();
  }

  onClickSubmit() {
    this.createInventoryService.onSubmit();
  }

  async openCalendarModal() {
    const date = this.createInventoryService.createInventoryForm.get('date')
      .value;
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: CalendarV2ModalComponent,
        rootPageParams: {
          title: 'i18n.common.selectYourDate',
          multiple: false,
          timeEnabled: false,
          lastSelectedDate: date,
          minDate: this.createInventoryService.inventoryMinDate,
          maxDate: this.createInventoryService.inventoryMaxDate,
        } as CalendarSetup,
      },
      'calendar-modal-desktop',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(this.handleCalendarModalDismiss);
    await modal.present();
  }

  handleCalendarModalDismiss = (data: any) => {
    if (data.data !== undefined)
      this.createInventoryService.setSelectedDate(data.data.selectedDate);
  };

  async openPopulateInventoryModal() {
    this.preparePopulateInventoryModal();
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage:
          this.platformService.platformType ===
          this.platformService.platformEnum.mobile
            ? PopulateInventoryMobileComponent
            : PopulateInventoryDesktopTabletComponent,
        rootPageParams: this.getPopulateInventoryModalRootParams(),
      },
      'modal-desktop-size-xl',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(this.handlePopulateInventoryModalDismiss);
    await modal.present();
  }

  preparePopulateInventoryModal = () => {
    this.createInventoryService.clearValues();
    this.createInventoryService.getRecentlyPurchasedProductData();
    this.createInventoryService.onSelectMasterList(111, false);
    this.createInventoryService.selectAllPastPurchaseProducts(false);
    this.createInventoryService.updateSelectedInventory('', false);
    this.setBreadcrumbs(
      this.translateService.instant(PageTitle.createInventory),
    );
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.createInventory),
    );
  };

  getPopulateInventoryModalRootParams() {
    return {
      selectedCreateInventoryOption:
        this.platformService.platformType ===
        this.platformService.platformEnum.mobile
          ? ({ code: CreateInventoryModal.populateInventory } as SubCategory)
          : ({ code: CreateInventoryModal.pastInventory } as SubCategory),
      inventories: this.createInventoryService.getInventories$(),
      shoppingLists: this.createInventoryService.getShoppingLists$(''),
      validTimes: this.createInventoryService.getValidRecentPurchaseDates$(),
      inventoryConstants: this.createInventoryService.getInventoryConstants$(),
    };
  }

  handlePopulateInventoryModalDismiss = (data: {
    data: CreateInventoryModalValues;
    role: string;
  }) => {
    if (
      !data.data ||
      (data.role !== undefined &&
        (data.role === 'gesture' || data.role === 'backdrop'))
    ) {
      this.createInventoryService.clearValues();
    } else {
      this.createInventoryService.setSelectedCreateInventoryOption(
        data.data.selectedCreateInventoryOption,
      );

      if (
        data.data.selectedCreateInventoryOption.code ===
        CreateInventoryModal.shoppingList
      ) {
        this.handleShoppingListSelection(data.data.selectedShoppingList);
      } else if (
        data.data.selectedCreateInventoryOption.code ===
        CreateInventoryModal.pastPurchase
      ) {
        this.handlePastPurchaseSelection(data.data.pastPurchaseDays);
      }
    }
  };

  handleInventorySelection(selectedInventory: InventoryViewModel) {
    this.setBreadcrumbs(this.translateService.instant(PageTitle.copyInventory));
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.copyInventory),
    );
    this.createInventoryService.setSelectedInventory(selectedInventory);
    this.createInventoryService.setSelectedCreateInventoryOption({
      code: CreateInventoryModal.pastInventory.valueOf(),
    });
  }

  handleShoppingListSelection(selectedShoppingList: ListsViewModel) {
    this.createInventoryService.setSelectedShoppingList(selectedShoppingList);
  }

  handlePastPurchaseSelection(pastPurchaseDays: string) {
    this.createInventoryService.setPastPurchaseDays(pastPurchaseDays);
    this.addToInventoryViewModel$ = this.createInventoryService.selectAddToInventoryViewModel$(
      this.platformService?.platformType,
    );
  }

  openSelectedProductsModal() {
    this.selectedProductsModalIsOpen$.next(true);
  }

  handleSelectedProductsModalDismiss(event: any) {
    this.selectedProductsModalIsOpen$.next(false);
    const numberOfProductsSelected =
      event?.detail?.data?.numberOfProductsSelected || 0;
    this.createInventoryService.setNumberOfSelectedProducts(
      numberOfProductsSelected,
    );
  }

  selectProduct(selectedProduct: SelectedProduct) {
    this.createInventoryService.selectPastPurchaseProduct(
      selectedProduct.productNumber,
      selectedProduct.value,
    );
  }

  selectMultipleProducts(selectedProducts: SelectedProduct[]) {
    this.createInventoryService.selectMultiplePastPurchaseProducts(
      selectedProducts.map(product => product.productNumber),
      true,
    );
  }

  selectAllProducts(value: boolean) {
    this.createInventoryService.selectAllPastPurchaseProducts(value);
  }

  handleNameChange(event: any) {
    this.createInventoryService.updateNameValidator(
      event?.currentTarget?.value ?? ('' as string),
    );
  }
}
